import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent,
  CardTitle 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  AlertCircle, 
  UnlockIcon, 
  RefreshCw, 
  Search,
  Clock,
  AlertTriangle,
  Lock,
  Activity
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { apiClient } from '../api/client';
import { DashboardLayout } from '../components/layout/DashboardLayout';

interface LockedUser {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_banned: boolean;
  ban_date: string;
  ban_expires_at?: string;
  ban_reason: string;
  campus_id?: {
    _id: string;
    name?: string;
    campus_name?: string;
  };
  school_id?: {
    _id: string;
    name?: string;
    school_name?: string;
  };
  recentFailedAttempts: Array<{
    timestamp: string;
    ip_address: string;
    device_info?: string;
  }>;
  isExpired: boolean;
}

interface UnlockStats {
  totalLocked: number;
  lockedToday: number;
  lockedThisWeek: number;
  expiredLocks: number;
  recentUnlocks: number;
  timestamp: string;
}

export const LockedUsersPage: React.FC = () => {
  const [lockedUsers, setLockedUsers] = useState<LockedUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showBulkUnlockModal, setShowBulkUnlockModal] = useState(false);
  const [userToUnlock, setUserToUnlock] = useState<LockedUser | null>(null);
  const [unlockReason, setUnlockReason] = useState('Manual unlock by admin');
  const [clearAttempts, setClearAttempts] = useState(true);
  const [stats, setStats] = useState<UnlockStats | null>(null);

  useEffect(() => {
    fetchLockedUsers();
    fetchStats();
  }, [currentPage, searchTerm]);

  const fetchLockedUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/admin/users/locked`, {
        params: {
          page: currentPage,
          limit: 20,
          search: searchTerm,
        },
      });
      setLockedUsers(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      const error = err as Error & { response?: { data?: { error?: string } } };
      console.error('Error fetching locked users:', error);
      alert(error.response?.data?.error || 'Failed to fetch locked users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get(`/api/admin/users/unlock-stats`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleUnlockUser = async () => {
    if (!userToUnlock) return;

    setLoading(true);
    try {
      await apiClient.post(
        `/api/admin/users/${userToUnlock._id}/unlock`,
        {
          reason: unlockReason,
          clearAttempts,
        }
      );
      alert(`Successfully unlocked ${userToUnlock.first_name} ${userToUnlock.last_name}`);
      setShowUnlockModal(false);
      setUserToUnlock(null);
      fetchLockedUsers();
      fetchStats();
    } catch (err) {
      const error = err as Error & { response?: { data?: { error?: string } } };
      alert(error.response?.data?.error || 'Failed to unlock user');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUnlock = async () => {
    if (selectedUsers.length === 0) return;

    setLoading(true);
    try {
      await apiClient.post(
        `/api/admin/users/bulk-unlock`,
        {
          userIds: selectedUsers,
          reason: unlockReason,
          clearAttempts,
        }
      );
      alert(`Successfully unlocked ${selectedUsers.length} users`);
      setShowBulkUnlockModal(false);
      setSelectedUsers([]);
      fetchLockedUsers();
      fetchStats();
    } catch (err) {
      const error = err as Error & { response?: { data?: { error?: string } } };
      alert(error.response?.data?.error || 'Failed to unlock users');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === lockedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(lockedUsers.map((user) => user._id));
    }
  };

  const openUnlockModal = (user: LockedUser) => {
    setUserToUnlock(user);
    setUnlockReason('Manual unlock by admin');
    setClearAttempts(true);
    setShowUnlockModal(true);
  };

  const openBulkUnlockModal = () => {
    setUnlockReason('Bulk unlock by admin');
    setClearAttempts(true);
    setShowBulkUnlockModal(true);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM dd, yyyy HH:mm');
  };

  const getStatusBadge = (user: LockedUser) => {
    if (user.isExpired) {
      return <Badge variant="secondary">Lock Expired</Badge>;
    }
    if (user.ban_expires_at) {
      return (
        <Badge variant="default">
          Expires {formatDistanceToNow(new Date(user.ban_expires_at), { addSuffix: true })}
        </Badge>
      );
    }
    return <Badge variant="destructive">Locked</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Locked Users Management</h1>
        <p className="text-gray-600">Manage users locked due to failed login attempts</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                <Lock className="w-4 h-4 inline mr-2" />
                Total Locked
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalLocked}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                <Clock className="w-4 h-4 inline mr-2" />
                Locked Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.lockedToday}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                <Activity className="w-4 h-4 inline mr-2" />
                This Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.lockedThisWeek}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Expired Locks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.expiredLocks}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Locked User Accounts</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openBulkUnlockModal}
                disabled={selectedUsers.length === 0}
              >
                <UnlockIcon className="w-4 h-4 mr-2" />
                Unlock Selected ({selectedUsers.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchLockedUsers();
                  fetchStats();
                }}
                disabled={loading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          ) : lockedUsers.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400" />
              <p className="mt-2 text-gray-600">No locked users found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Checkbox
                        checked={selectedUsers.length === lockedUsers.length && lockedUsers.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>School/Campus</TableHead>
                    <TableHead>Locked Since</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Failed Attempts</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lockedUsers.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.includes(user._id)}
                          onCheckedChange={() => handleSelectUser(user._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                          </div>
                          <span>{user.first_name} {user.last_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <div>
                          {user.school_id && (
                            <div className="text-sm">
                              {user.school_id.name || user.school_id.school_name || 'Unknown School'}
                            </div>
                          )}
                          {user.campus_id && (
                            <div className="text-xs text-gray-500">
                              {user.campus_id.name || user.campus_id.campus_name || 'Unknown Campus'}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(user.ban_date)}</TableCell>
                      <TableCell>{getStatusBadge(user)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="cursor-help">
                          {user.recentFailedAttempts.length} attempts
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => openUnlockModal(user)}
                        >
                          <UnlockIcon className="w-4 h-4 mr-1" />
                          Unlock
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Unlock Single User Modal */}
      <Dialog open={showUnlockModal} onOpenChange={setShowUnlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock User Account</DialogTitle>
            <DialogDescription>
              {userToUnlock && (
                <>
                  You are about to unlock the account for:
                  <br />
                  <strong>
                    {userToUnlock.first_name} {userToUnlock.last_name}
                  </strong>
                  <br />
                  <span className="text-sm">{userToUnlock.email}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="unlock-reason">Reason for unlock</Label>
              <Textarea
                id="unlock-reason"
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="Enter reason for unlocking this account..."
                rows={3}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="clear-attempts"
                checked={clearAttempts}
                onCheckedChange={(checked) => setClearAttempts(checked as boolean)}
              />
              <Label htmlFor="clear-attempts">
                Clear all failed login attempts
              </Label>
            </div>
            
            {userToUnlock && userToUnlock.recentFailedAttempts.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-yellow-600" />
                <span className="text-sm">
                  This user has {userToUnlock.recentFailedAttempts.length} recent failed login attempts
                </span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlockModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUnlockUser} disabled={loading}>
              {loading ? 'Unlocking...' : 'Unlock Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Unlock Modal */}
      <Dialog open={showBulkUnlockModal} onOpenChange={setShowBulkUnlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Unlock Users</DialogTitle>
            <DialogDescription>
              You are about to unlock {selectedUsers.length} user accounts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="bulk-unlock-reason">Reason for unlock</Label>
              <Textarea
                id="bulk-unlock-reason"
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="Enter reason for unlocking these accounts..."
                rows={3}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="bulk-clear-attempts"
                checked={clearAttempts}
                onCheckedChange={(checked) => setClearAttempts(checked as boolean)}
              />
              <Label htmlFor="bulk-clear-attempts">
                Clear all failed login attempts for selected users
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUnlockModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkUnlock} disabled={loading}>
              {loading ? 'Unlocking...' : `Unlock ${selectedUsers.length} Accounts`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};